// The entry for the three portrait pages (home, About me, and About this site). It starts
// each part of the page and wires them together.

// The portrait goes first: it starts downloading its data as it upgrades, and the field's
// setup would hold that request back.
import { JrsmPortrait } from "../elements/jrsm-portrait.js";
import { JrsmGlyphField } from "../elements/jrsm-glyph-field.js";
import { startAccents } from "../effects/accents.js";
import "../elements/jrsm-video.js";
import { PaletteWaves } from "../waves/palette-waves.js";
import { JrsmIntroCopy } from "../elements/jrsm-intro-copy.js";
import { waveRewriteCount } from "../content/intro-copy.js";
import { showPage } from "./page-reveal.js";
import { FOCUS_CLEARANCE } from "../effects/halftone-layer.js";
import { DISSOLVE_DISTANCE, startTextEffects } from "../effects/text-effects.js";
import { currentTheme } from "./theme.js";
import { addObliqueFace } from "./oblique-face.js";
import { handOffSharedParts } from "./transition-handoff.js";
import { loopOnce } from "./loop-once.js";
import { startShelfMark } from "./shelf-mark.js";

function must<T extends Element>(selector: string, type: new () => T): T {
  const element = document.querySelector(selector);
  if (!(element instanceof type)) throw new Error(`home: missing ${selector}`);
  return element;
}

// The end mark scrolls to the top without adding `#top` to the address or the history. The
// scroll follows the page's `scroll-behavior`, so with reduced motion it jumps. Focus moves
// to the heading (the name, on home), so the next Tab starts at the top, like it would after
// following the fragment link.
const heading = must("h1", HTMLHeadingElement);
// Link marks that loop on hover: the arrow on "What else?" and the external-link marks.
for (const link of document.querySelectorAll("a:has(.cta-arrow, .external)"))
  if (link instanceof HTMLAnchorElement) loopOnce(link);
// Each shelf's single traveling mark (About me).
for (const shelf of document.querySelectorAll(".shelf"))
  if (shelf instanceof HTMLElement) startShelfMark(shelf);
must(".end-mark", HTMLAnchorElement).addEventListener("click", (event) => {
  event.preventDefault();
  heading.focus({ preventScroll: true });
  window.scrollTo({ top: 0 });
});

// On home, clicking a phrase rewrites it, and each click or gust wave rewrites a few as the
// wave reaches them (elements/jrsm-intro-copy.ts). The about pages have no bio.
const bio = document.querySelector("jrsm-intro-copy");
if (bio !== null && !(bio instanceof JrsmIntroCopy)) throw new Error("home: bio not defined");

// The field has already started on upgrade, around the column its markup names. Its shaders
// link without holding up the other parts, and it joins the waves once they're ready
// (elements/jrsm-glyph-field.ts).
const carve = must("#carve", HTMLElement);
const field = must("jrsm-glyph-field", JrsmGlyphField);

// The text dissolves under the face, and every wave rolls through the words' weight.
const text = startTextEffects(carve);

// Each wave repaints the field, the accents, and the favicon with another palette (below).
const palettes = new PaletteWaves();
// The corner plates are fixed to the viewport, outside the column, so they're listed here.
const accents = startAccents(
  carve,
  [...document.querySelectorAll("jrsm-settings, .about-mark")].filter(
    (element) => element instanceof HTMLElement,
  ),
);

// The stopped portrait's visible height, so focused content doesn't land under it
// (`--portrait-stuck` in site.scss). The sticky top is negative, so the visible part is the
// top plus the height, and the turned portrait's lower corner pokes out below the box by half
// the difference between the turned box's height and its side. This applies without WebGL
// too, since the static print covers content just the same.
//
// The portrait only stops if the viewport fits it, a focused player's clearance below it
// (`FOCUS_CLEARANCE`), and the tallest player with its rail, and if the portrait and its fade
// take up at most `PORTRAIT_SHARE` of the viewport's height. On a shorter viewport (a phone on
// its side, or high zoom) it scrolls with the page (`data-portrait="flow"`), so nothing hides
// under it.
//
// The observer also reports the first size. A viewport that only changes height doesn't
// resize the portrait, so the window's resize event runs this too.
const PORTRAIT_SHARE = 1 / 3;
const portrait = must("jrsm-portrait", JrsmPortrait);
const players = [...document.querySelectorAll("jrsm-video")];
function placePortrait(): void {
  // A portrait that's not in the page has no box. The next connection measures again.
  if (!portrait.isConnected) return;
  const root = document.documentElement;
  const turned = portrait.getBoundingClientRect().height;
  // The sticky top only applies while the portrait is stuck, but its computed value is
  // always there.
  const visible =
    Number.parseFloat(getComputedStyle(portrait).top) + (portrait.offsetHeight + turned) / 2;
  const unit = Math.max(0, ...players.map((player) => player.offsetHeight));
  const flow =
    window.innerHeight < visible + FOCUS_CLEARANCE + unit ||
    visible + DISSOLVE_DISTANCE > window.innerHeight * PORTRAIT_SHARE;
  if (flow) root.dataset.portrait = "flow";
  else delete root.dataset.portrait;
  root.style.setProperty("--portrait-stuck", `${flow ? 0 : visible}px`);
  // Keyboard focus on a player scrolls it clear of the stopped face. Nothing covers a
  // flowing page.
  for (const player of players) player.focusClearance = flow ? 0 : visible + FOCUS_CLEARANCE;
}
new ResizeObserver(placePortrait).observe(portrait);
window.addEventListener("resize", placePortrait, { passive: true });

// A click or gust wave turns the field to dust, which blows away from the butterfly over the
// field's new palette (`sweep()` in field.ts). The palette changes as the dust covers the
// field. The portrait and the content stay out of the dust, so it never blows across
// anything you're reading. One sweep at a time: while one runs, new waves are held, and a
// click just flaps the wings.
let sweeping = false;
// New waves still waiting for their first ring, which tells the dust where the butterfly is.
const dusting = new Set<number>();

portrait.addEventListener("jrsm-wave", ({ detail: wave }) => {
  const count = waveRewriteCount(wave.source, wave.level);
  if (count > 0) bio?.startWave(wave.id, count);
  // A theme wave changes the palette under the theme's crossfade, with no dust.
  if (wave.source === "theme") palettes.repaint(currentTheme());
  else dusting.add(wave.id);
});
portrait.hooks = {
  onOutline: (outline) => text.setOutline(outline),
  wavesHeld: () => sweeping,
  onWaves: (rings) => {
    // A wave's first ring: the field copies its next frame for the dust.
    for (const ring of rings) {
      if (!dusting.delete(ring.id)) continue;
      sweeping = true;
      void field
        .sweep({ x: ring.originX, y: ring.originY }, () => palettes.repaint(currentTheme()))
        .finally(() => (sweeping = false));
    }
    const painting = palettes.update(rings);
    // The field's gust ages, or null when there aren't any (then everything uses the
    // fallback front; `GUST_GRAINS` in field.ts).
    const ages = field.ages();
    // The bio and the accents measure their elements, so they have to go before the text
    // effects restyle any words.
    bio?.reachWaves(rings, ages);
    accents.setWaves(rings, painting, ages);
    field.setWaves(rings, painting);
    text.setWaves(rings, ages);
  },
};

// A page transition keeps the old page's field and portrait on top of this page's until
// these have drawn or failed (transition-handoff.ts). A page restored from the back/forward
// cache drew them long ago, so a transition that reveals it ends the hold right away.
const partsShown = Promise.allSettled([field.ready, portrait.ready]);
void partsShown.then(() => handOffSharedParts());
addEventListener("pagereveal", (event) => {
  if (event.viewTransition) void partsShown.then(() => handOffSharedParts());
});

// Resolves once the window's load event has fired.
function pageLoaded(): Promise<void> {
  if (document.readyState === "complete") return Promise.resolve();
  return new Promise((resolve) => window.addEventListener("load", () => resolve(), { once: true }));
}

// The page shows once its parts and its upright font have drawn (or failed), so nothing
// pops in. If one never settles, the head script's time limit shows the page anyway.
//
// The slanted face only sets the project cards' footnotes, so the page doesn't wait for it.
// When it shared the connection with the upright font, its download held the cover back.
//
// The X-ray only shows during a wave, so it loads after the page shows and after the load
// event, and takes nothing away from the first view.
const pageFont = document.fonts.load('1em "Recursive"');
void Promise.allSettled([field.ready, portrait.ready, pageFont])
  .then(showPage)
  .then(addObliqueFace)
  .then(pageLoaded)
  .then(() => portrait.loadXray());
